import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Copy, Download, AlertTriangle, Plus, Info } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Comparativo para o cliente (Adnéia, 13/10/2026).
 *
 * O atendente propõe os similares; aqui a tela monta uma IMAGEM com o original
 * e as alternativas lado a lado (foto, nome, marca e a ficha técnica da VTEX),
 * para colar no WhatsApp junto do texto padrão. A loja VTEX pede login, então o
 * cliente não abre link de loja: recebe a comparação pronta.
 *
 * Decisões dela (página de decisão de 13/10): imagem para anexar; SEM preço; o
 * atendente analisa e escolhe o que propor; a ficha do original zerado vem da
 * rota /produtos/ficha (VTEX administrativa); o que faltar aparece em alerta
 * para o atendente completar (célula editável) ou tirar a linha.
 *
 * Depois do 1º teste dela (13/10, fones ODRPR2065/2066/2068): a imagem deixa
 * claro o que muda em relação ao pedido. Linha "O que muda" (automática e
 * editável), diferenças em laranja e "Mesmo produto, muda só a cor" quando o
 * nome padronizado da ficha bate e só a cor difere. O texto que acompanha a
 * imagem nasce aqui, sem os links de imagem, pedindo para o cliente olhar a
 * imagem e responder com o número.
 *
 * Props: original {sku, nome, id}, similares [{sku, nome, id}], entrega,
 * abertura (início da frase do texto), onClose.
 * Nada aqui grava no ELO nem envia nada: só lê a ficha e gera a imagem e o texto.
 */

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const FALTA = '?';
// Campos que entram sempre que algum produto tiver; os demais entram quando
// aparecem em 2 ou mais produtos (o resto fica em "Adicionar linha").
// Diâmetro e Capacidade (15/10/2026): frigideira de 0,6 L saiu ao lado da de
// 1,35 L e a imagem não dizia o tamanho.
const PRIORIDADE = ['Diâmetro', 'Capacidade', 'Voltagem', 'Potência', 'Velocidades', 'Temperaturas', 'Jato de ar frio'];
// Tamanho bem menor que o pedido, a mesma regra do Propor Similar (Adnéia, 15/10/2026):
// diâmetro menor ou capacidade mais de 20% menor.
const TOLERANCIA_CAPACIDADE = 0.2;
const COR = {
  navy: '#1d2c5d', laranja: '#e5621a', texto: '#1b2133', cinza: '#5b6375',
  linha: '#e3e6ee', faixa: '#f5f6fa', branco: '#ffffff',
};
const FONTE = '"Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

// Foto menor (300px) para carregar rápido; a VTEX redimensiona pela URL.
const fotoMenor = (url) => (url || '').replace(/\/arquivos\/ids\/(\d+)(-\d+-\d+)?\//, '/arquivos/ids/$1-300-300/');

function carregarImagem(url) {
  return new Promise((resolve) => {
    if (!url) { resolve(null); return; }
    const im = new Image();
    im.crossOrigin = 'anonymous';   // a VTEX responde Access-Control-Allow-Origin: *
    im.onload = () => resolve(im);
    im.onerror = () => resolve(null);
    im.src = fotoMenor(url);
  });
}

function quebrar(ctx, texto, largura, maxLinhas) {
  const palavras = String(texto || '').split(/\s+/).filter(Boolean);
  const linhas = [];
  let atual = '';
  palavras.forEach((p) => {
    const tentativa = atual ? `${atual} ${p}` : p;
    if (!atual || ctx.measureText(tentativa).width <= largura) atual = tentativa;
    else { linhas.push(atual); atual = p; }
  });
  if (atual) linhas.push(atual);
  if (linhas.length <= maxLinhas) return linhas.length ? linhas : [''];
  const cortadas = linhas.slice(0, maxLinhas);
  let ultima = cortadas[maxLinhas - 1];
  while (ultima.length > 1 && ctx.measureText(`${ultima}…`).width > largura) ultima = ultima.slice(0, -1);
  cortadas[maxLinhas - 1] = `${ultima}…`;
  return cortadas;
}

const normVolt = (v) => {
  const s = String(v || '').toUpperCase();
  if (!s || s === FALTA) return '';
  if (s.includes('BIVOLT')) return 'BIVOLT';
  const m = s.match(/(110|127|220)/);
  if (m) return m[1] === '110' ? '127' : m[1];
  return s.trim();
};

const semAcento = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '');
const norm = (v) => semAcento(v).toLowerCase().replace(/\s+/g, ' ').trim();
const conhecido = (v) => !!(v && v !== FALTA && String(v).trim());
// "24cm" e "24 cm", "1.2 L" e "1,2 L" são o mesmo valor: não saem em laranja.
const compacto = (v) => norm(v).replace(/\s+/g, '').replace(/,/g, '.');
// Só compara a mesma unidade (1a revisão de 7918cf1): "10 xícaras" contra "1,8 L"
// ou "240 mm" contra "22 cm" não acendem o aviso. Sem unidade = sem comparação.
const cmDe = (v) => {
  const m = String(v || '').match(/(\d+(?:[.,]\d+)?)\s*cm\b/i);
  return m ? parseFloat(m[1].replace(',', '.')) : null;
};
const litrosDe = (v) => {
  const ml = String(v || '').match(/(\d+(?:[.,]\d{3})*)\s*ml\b/i);
  if (ml) return parseInt(ml[1].replace(/[.,]/g, ''), 10) / 1000;   // "1.200 ml" = 1,2 L
  const l = String(v || '').match(/(\d+(?:[.,]\d+)?)\s*(?:l|lt|lts|litros?)\b/i);
  return l ? parseFloat(l[1].replace(',', '.')) : null;
};
// O nome já está na linha "Produto": campo de nome da ficha não vira linha.
const ehCampoDeNome = (k) => /^(nome|titulo)( do produto)?$/.test(norm(k));

// "Mesmo produto" é uma afirmação ao cliente: só vale com prova forte. Os dois
// nomes padronizados da ficha têm de ser IGUAIS palavra por palavra, tirando só
// as palavras do campo Cor dos dois. Ex.: "FONE ... TRUE WIRELESS - BRANCO" x
// "... - DOURADO" (Cor Branco / Dourado). Não passa: "TWS" x "TWS PRO COM
// CANCELAMENTO", "510BT" x "520BT", 127V x 220V (revisão de e7c2d3b).
const VAZIAS = new Set(['de', 'da', 'do', 'das', 'dos', 'com', 'e', 'para', 'em', 'a', 'o']);
const tokensNome = (nome, tirar) => new Set(norm(nome).split(/[^a-z0-9+]+/)
  .filter((t) => t && !VAZIAS.has(t) && !tirar.has(t)));
function mesmoProduto(nomeA, nomeB, coresDosDois) {
  if (!nomeA || !nomeB) return false;
  const A = tokensNome(nomeA, coresDosDois); const B = tokensNome(nomeB, coresDosDois);
  if (A.size < 2 || A.size !== B.size) return false;
  return [...A].every((t) => B.has(t));
}

// Frase da linha "O que muda" de cada alternativa (vai na imagem e no texto).
// diffs = TODOS os campos conhecidos dos dois lados que diferem (inclusive linhas
// tiradas da imagem); incerto = algum campo com valor de um lado só. A máquina só
// afirma igualdade com prova forte (mesmo); sem ela, manda comparar na imagem
// (2ª revisão de e7c2d3b: "mesmas características" saía quase sem dado).
function resumoDe(diffs, mesmo, incerto) {
  const lista = diffs.map((d) => `${d.rot.toLowerCase()} (${d.valor})`).join(', ');
  if (mesmo && diffs.length === 1 && norm(diffs[0].rot) === 'cor') return `Mesmo produto, muda só a cor (${diffs[0].valor})`;
  if (mesmo && !diffs.length && !incerto) return 'Mesmo produto';
  if (!diffs.length) return 'Compare as informações na imagem';
  return mesmo ? `Muda: ${lista}` : `Diferenças na ficha: ${lista}`;
}
const minuscula = (s) => (s ? s.charAt(0).toLowerCase() + s.slice(1) : s);
const saudacao = () => { const h = new Date().getHours(); return h < 12 ? 'Bom dia' : (h < 18 ? 'Boa tarde' : 'Boa noite'); };
const RODAPE = 'Imagens ilustrativas. Em laranja, o que muda em relação ao seu pedido. Responda com o número da alternativa que preferir.';

// Desenha a imagem que vai ao cliente. Sem preço. "?" sai como "não informado".
// resumo: sku -> frase "O que muda" (só alternativas); destaque: rótulo -> sku -> true
// para a célula que difere do pedido (sai em laranja).
export function desenharComparativo({ produtos, linhas, valores, imagens, resumo = {}, destaque = {} }) {
  const ESC = 2;
  const PAD = 20; const COL_ROT = 140; const COL = 220; const FOTO = 150; const LH = 19;
  const W = PAD * 2 + COL_ROT + COL * produtos.length;
  const fNome = `600 15px ${FONTE}`; const fTxt = `14px ${FONTE}`; const fVazio = `italic 13px ${FONTE}`;
  const fRot = `500 13px ${FONTE}`; const fId = `12px ${FONTE}`; const fDestaque = `700 14px ${FONTE}`;
  const fRodape = `12px ${FONTE}`;

  const med = document.createElement('canvas').getContext('2d');
  med.font = fNome;
  const nomes = produtos.map((p) => quebrar(med, p.nome || p.sku, COL - 24, 3));
  const hNome = Math.max(...nomes.map((l) => l.length)) * LH + 36;
  med.font = fDestaque;
  const resumos = produtos.map((p, i) => (i === 0 || !String(resumo[p.sku] || '').trim()
    ? [] : quebrar(med, resumo[p.sku], COL - 24, 3)));
  const temResumo = resumos.some((l) => l.length);
  const hResumo = temResumo ? Math.max(...resumos.map((l) => l.length)) * LH + 18 : 0;
  const celulas = linhas.map((rot) => produtos.map((p) => {
    const v = (valores[rot] || {})[p.sku];
    const vazio = !v || v === FALTA || !String(v).trim();
    const muda = !vazio && !!(destaque[rot] && destaque[rot][p.sku]);
    med.font = vazio ? fVazio : (muda ? fDestaque : fTxt);
    return { vazio, muda, linhas: quebrar(med, vazio ? 'não informado' : v, COL - 24, 3) };
  }));
  med.font = fRot;
  const rotulos = linhas.map((rot) => quebrar(med, rot, COL_ROT - 16, 2));
  const hLinhas = linhas.map((_, i) => Math.max(rotulos[i].length, ...celulas[i].map((c) => c.linhas.length)) * LH + 18);
  med.font = fRodape;
  const rodape = quebrar(med, RODAPE, W - PAD * 2, 3);
  const H_TOPO = 56; const H_CAB = 34; const H_FOTO = FOTO + 24; const H_RODAPE = rodape.length * 17 + 26;
  const H = H_TOPO + H_CAB + H_FOTO + hNome + hResumo + hLinhas.reduce((a, b) => a + b, 0) + H_RODAPE;

  const cv = document.createElement('canvas');
  cv.width = W * ESC; cv.height = H * ESC;
  const ctx = cv.getContext('2d');
  ctx.scale(ESC, ESC);
  ctx.fillStyle = COR.branco; ctx.fillRect(0, 0, W, H);
  const xCol = (i) => PAD + COL_ROT + COL * i;
  const regua = (yy) => {
    ctx.strokeStyle = COR.linha; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(PAD, yy + 0.5); ctx.lineTo(W - PAD, yy + 0.5); ctx.stroke();
  };

  // Faixa do topo
  ctx.fillStyle = COR.navy; ctx.fillRect(0, 0, W, H_TOPO);
  ctx.fillStyle = COR.branco; ctx.textBaseline = 'middle';
  ctx.font = `700 17px ${FONTE}`; ctx.fillText('Atendimento WeConnect', PAD, H_TOPO / 2);
  ctx.font = `14px ${FONTE}`; ctx.textAlign = 'right';
  ctx.fillText('Alternativas para o seu pedido', W - PAD, H_TOPO / 2);
  ctx.textAlign = 'left';
  let y = H_TOPO;

  // Cabeçalho das colunas: a numeração bate com a do texto padrão (1), 2)...)
  ctx.fillStyle = COR.faixa; ctx.fillRect(0, y, W, H_CAB);
  ctx.font = `700 12px ${FONTE}`;
  // Tudo em azul: o laranja da imagem fica só para o que muda (diz o rodapé).
  produtos.forEach((p, i) => {
    ctx.fillStyle = COR.navy;
    ctx.fillText(i === 0 ? 'SEU PEDIDO' : `ALTERNATIVA ${i}`, xCol(i) + 12, y + H_CAB / 2);
  });
  y += H_CAB;

  // Fotos
  produtos.forEach((p, i) => {
    const x = xCol(i) + 12; const yy = y + 12;
    ctx.strokeStyle = COR.linha; ctx.lineWidth = 1; ctx.strokeRect(x + 0.5, yy + 0.5, FOTO, FOTO);
    const im = imagens[p.sku];
    if (im && im.width && im.height) {
      const r = Math.min((FOTO - 8) / im.width, (FOTO - 8) / im.height);
      const w = im.width * r; const h = im.height * r;
      ctx.drawImage(im, x + (FOTO - w) / 2, yy + (FOTO - h) / 2, w, h);
    } else {
      ctx.fillStyle = COR.cinza; ctx.font = `12px ${FONTE}`; ctx.textAlign = 'center';
      ctx.fillText('sem foto', x + FOTO / 2, yy + FOTO / 2); ctx.textAlign = 'left';
    }
  });
  y += H_FOTO; regua(y);

  // Nome e ID (o mesmo ID que vai no texto)
  ctx.textBaseline = 'top';
  ctx.fillStyle = COR.cinza; ctx.font = fRot; ctx.fillText('Produto', PAD, y + 10);
  produtos.forEach((p, i) => {
    ctx.fillStyle = COR.texto; ctx.font = fNome;
    nomes[i].forEach((l, k) => ctx.fillText(l, xCol(i) + 12, y + 10 + k * LH));
    if (p.id) {
      ctx.fillStyle = COR.cinza; ctx.font = fId;
      ctx.fillText(`ID ${p.id}`, xCol(i) + 12, y + 12 + nomes[i].length * LH);
    }
  });
  y += hNome; regua(y);

  // O que muda em relação ao pedido (só nas alternativas), em laranja
  if (temResumo) {
    ctx.fillStyle = COR.cinza; ctx.font = fRot; ctx.fillText('O que muda', PAD, y + 9);
    ctx.fillStyle = COR.laranja; ctx.font = fDestaque;
    resumos.forEach((ls, i) => ls.forEach((l, k) => ctx.fillText(l, xCol(i) + 12, y + 9 + k * LH)));
    y += hResumo; regua(y);
  }

  // Ficha: o que difere do pedido sai em laranja e negrito
  linhas.forEach((rot, r) => {
    ctx.fillStyle = COR.cinza; ctx.font = fRot;
    rotulos[r].forEach((l, k) => ctx.fillText(l, PAD, y + 9 + k * LH));
    celulas[r].forEach((c, i) => {
      ctx.fillStyle = c.vazio ? COR.cinza : (c.muda ? COR.laranja : COR.texto);
      ctx.font = c.vazio ? fVazio : (c.muda ? fDestaque : fTxt);
      c.linhas.forEach((l, k) => ctx.fillText(l, xCol(i) + 12, y + 9 + k * LH));
    });
    y += hLinhas[r]; regua(y);
  });

  ctx.fillStyle = COR.cinza; ctx.font = fRodape;
  rodape.forEach((l, k) => ctx.fillText(l, PAD, y + 13 + k * 17));
  return cv;
}

export default function ComparativoCliente({
  original, similares = [], entrega = '',
  abertura = 'Infelizmente, tivemos uma falha sistêmica no item', onClose,
}) {
  const { getAuthHeader } = useAuth();
  // Sem o SKU do original não se monta a imagem: a coluna 0 é sempre "SEU PEDIDO"
  // e a numeração das alternativas tem de bater com a do texto padrão (1), 2)...).
  const semOriginal = !(original && original.sku);
  // Congela a lista na abertura: o modal pai recria os arrays a cada render.
  const [produtos] = useState(() => (semOriginal ? [] : [original, ...similares.filter((p) => p && p.sku)])
    .map((p) => ({ sku: String(p.sku).toUpperCase(), nome: p.nome || p.sku, id: p.id ? String(p.id) : '' })));
  const [fichas, setFichas] = useState({});     // sku -> resposta de /produtos/ficha
  const [imagens, setImagens] = useState({});   // sku -> Image | null
  const [linhas, setLinhas] = useState(null);   // rótulos que vão na imagem, na ordem
  const [extras, setExtras] = useState([]);     // rótulos disponíveis fora da imagem
  const [valores, setValores] = useState({});   // rótulo -> sku -> valor
  const [preview, setPreview] = useState('');
  const [verDesc, setVerDesc] = useState({});
  const [resumoManual, setResumoManual] = useState({});   // sku -> "O que muda" editado pelo atendente

  const carregado = produtos.length > 0 && produtos.every((p) => fichas[p.sku] !== undefined);
  const fotosProntas = produtos.every((p) => imagens[p.sku] !== undefined);

  useEffect(() => {
    let vivo = true;
    produtos.forEach((p) => {
      axios.get(`${API_URL}/api/produtos/ficha/${encodeURIComponent(p.sku)}`, { headers: getAuthHeader() })
        .then((r) => { if (vivo) setFichas((prev) => ({ ...prev, [p.sku]: r.data || { ficha: {} } })); })
        .catch(() => { if (vivo) setFichas((prev) => ({ ...prev, [p.sku]: { ficha: {}, erro: true } })); });
    });
    return () => { vivo = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Quando as fichas chegam: escolhe as linhas e carrega as fotos (uma vez só).
  useEffect(() => {
    if (!carregado || linhas) return;
    const contagem = {}; const ordem = [];
    produtos.forEach((p) => Object.keys((fichas[p.sku] || {}).ficha || {}).forEach((k) => {
      if (ehCampoDeNome(k)) return;
      if (!(k in contagem)) { contagem[k] = 0; ordem.push(k); }
      contagem[k] += 1;
    }));
    const prioridade = PRIORIDADE.filter((k) => k in contagem);
    const resto = ordem.filter((k) => !PRIORIDADE.includes(k)).sort((a, b) => contagem[b] - contagem[a]);
    const visiveis = ['Marca', ...prioridade, ...resto.filter((k) => contagem[k] >= 2)];
    const fora = resto.filter((k) => contagem[k] < 2);
    const v = {};
    [...visiveis, ...fora].forEach((rot) => {
      v[rot] = {};
      produtos.forEach((p) => {
        const f = fichas[p.sku] || {};
        const val = rot === 'Marca' ? f.marca : (f.ficha || {})[rot];
        v[rot][p.sku] = (val && String(val).trim()) || FALTA;
      });
    });
    setValores(v); setLinhas(visiveis); setExtras(fora);
    Promise.all(produtos.map((p) => carregarImagem((fichas[p.sku] || {}).image_url).then((im) => [p.sku, im])))
      .then((pares) => setImagens(Object.fromEntries(pares)));
  }, [carregado]); // eslint-disable-line react-hooks/exhaustive-deps

  // O que muda em cada alternativa, comparando com o pedido nas linhas da imagem.
  // Recalcula a cada render (o atendente edita valores); o atendente pode
  // sobrescrever a frase (resumoManual).
  const orig = produtos[0];
  const destaque = {};
  const resumoAuto = {};
  if (linhas && orig) {
    const coresDe = (sku) => norm((valores.Cor || {})[sku]).split(/[^a-z0-9]+/).filter(Boolean);
    const marcaO = (valores.Marca || {})[orig.sku];
    // Todos os campos: os da imagem primeiro, depois os tirados e os de "Adicionar linha".
    const campos = [...linhas, ...Object.keys(valores).filter((k) => !linhas.includes(k))];
    produtos.slice(1).forEach((p) => {
      const diffs = campos
        .filter((rot) => {
          const a = (valores[rot] || {})[orig.sku]; const b = (valores[rot] || {})[p.sku];
          return conhecido(a) && conhecido(b) && compacto(a) !== compacto(b);
        })
        .map((rot) => ({ rot, valor: valores[rot][p.sku] }));
      // O laranja só aparece nas linhas da imagem (o desenho só percorre `linhas`).
      diffs.forEach((d) => { destaque[d.rot] = { ...(destaque[d.rot] || {}), [p.sku]: true }; });
      const marcaP = (valores.Marca || {})[p.sku];
      const fo = fichas[orig.sku] || {}; const fp = fichas[p.sku] || {};
      // Prova forte: marca conhecida e igual + nome padronizado da ficha nos dois
      // (sem cair no nome de exibição) + nomes iguais tirando só as cores.
      const mesmo = conhecido(marcaO) && conhecido(marcaP) && norm(marcaO) === norm(marcaP)
        && mesmoProduto(fo.nome_ficha, fp.nome_ficha, new Set([...coresDe(orig.sku), ...coresDe(p.sku)]));
      const incerto = Object.keys(valores).some((k) => conhecido((valores[k] || {})[orig.sku]) !== conhecido((valores[k] || {})[p.sku]));
      resumoAuto[p.sku] = resumoDe(diffs, mesmo, incerto);
    });
  }
  const resumo = {};
  produtos.slice(1).forEach((p) => {
    resumo[p.sku] = resumoManual[p.sku] !== undefined ? resumoManual[p.sku] : (resumoAuto[p.sku] || '');
  });

  // Texto que vai junto com a imagem (sem os links de imagem: a imagem já mostra tudo).
  const alts = produtos.slice(1);
  const textoCliente = orig ? [
    saudacao(),
    `${abertura} ${orig.nome}${orig.id ? ` (${orig.id})` : ''}${entrega ? ` - ${entrega}` : ''}.`,
    alts.length > 1
      ? 'Para você não ficar sem o produto, separamos algumas alternativas. Na imagem que enviamos, o seu pedido está na primeira coluna e as alternativas ao lado; em laranja, o que muda:'
      : 'Para você não ficar sem o produto, separamos uma alternativa. Na imagem que enviamos, o seu pedido está na primeira coluna e a alternativa ao lado; em laranja, o que muda:',
    ...alts.map((p, i) => `${i + 1}) ${p.nome}${p.id ? ` (ID: ${p.id})` : ''}${String(resumo[p.sku] || '').trim() ? ` - ${minuscula(resumo[p.sku].trim())}` : ''}`),
    alts.length > 1
      ? 'Por favor, veja a imagem e responda com o número da alternativa que preferir.'
      : 'Por favor, veja a imagem e confirme se aceita a substituição.',
    'Aguardamos retorno e seguimos à disposição.',
    'Atenciosamente!',
    'Atendimento Weconnect',
  ].join('\n') : '';

  // Redesenha a prévia quando o atendente para de digitar (não a cada tecla).
  useEffect(() => {
    if (!linhas) return undefined;
    const t = setTimeout(() => {
      try { setPreview(desenharComparativo({ produtos, linhas, valores, imagens, resumo, destaque }).toDataURL('image/png')); }
      catch (e) { setPreview(''); }
    }, 250);
    return () => clearTimeout(t);
  }, [linhas, valores, imagens, resumoManual]); // eslint-disable-line react-hooks/exhaustive-deps

  const mudar = (rot, sku, val) => setValores((prev) => ({
    ...prev, [rot]: { ...(prev[rot] || {}), [sku]: val.trim() === '' ? FALTA : val },
  }));
  const tirarLinha = (rot) => { setLinhas((prev) => prev.filter((r) => r !== rot)); setExtras((prev) => [...prev, rot]); };
  const porLinha = (rot) => { setExtras((prev) => prev.filter((r) => r !== rot)); setLinhas((prev) => [...prev, rot]); };

  const gerarBlob = () => new Promise((resolve, reject) => {
    try {
      desenharComparativo({ produtos, linhas: linhas || [], valores, imagens, resumo, destaque })
        .toBlob((b) => (b ? resolve(b) : reject(new Error('imagem vazia'))), 'image/png');
    } catch (e) { reject(e); }
  });

  const copiarImagem = async () => {
    try {
      if (!navigator.clipboard || !window.ClipboardItem) throw new Error('sem suporte');
      // Promise dentro do ClipboardItem: o Chrome mantém o gesto do clique.
      await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': gerarBlob() })]);
      toast.success('Imagem copiada. No WhatsApp, cole com Ctrl+V.');
    } catch (e) {
      toast.error('Este navegador não deixou copiar a imagem. Use "Baixar imagem".');
    }
  };

  const baixarImagem = async () => {
    try {
      const blob = await gerarBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `comparativo-${produtos[0]?.sku || 'similar'}.png`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) {
      toast.error('Não consegui gerar a imagem.');
    }
  };

  const copiarTexto = async () => {
    try {
      await navigator.clipboard.writeText(textoCliente);
      toast.success('Texto copiado. Cole no WhatsApp junto da imagem.');
    } catch (e) {
      toast.error('Não consegui copiar o texto. Selecione e copie à mão.');
    }
  };

  // Alertas para o atendente (não vão na imagem)
  const faltas = (linhas || []).flatMap((rot) => produtos
    .filter((p) => ((valores[rot] || {})[p.sku] || FALTA) === FALTA)
    .map((p) => ({ rot, p })));
  const vOrig = normVolt((valores.Voltagem || {})[produtos[0]?.sku]);
  const voltDiferente = vOrig ? produtos.slice(1).filter((p) => {
    const v = normVolt((valores.Voltagem || {})[p.sku]);
    return v && v !== vOrig && v !== 'BIVOLT';
  }) : [];
  const tamanhoDe = (sku) => ({
    d: cmDe((valores['Diâmetro'] || {})[sku]), c: litrosDe((valores.Capacidade || {})[sku]),
  });
  const tamOrig = produtos[0] ? tamanhoDe(produtos[0].sku) : {};
  const menores = produtos.slice(1).filter((p) => {
    const t = tamanhoDe(p.sku);
    return (tamOrig.d && t.d && t.d < tamOrig.d)
      || (tamOrig.c && t.c && t.c < tamOrig.c * (1 - TOLERANCIA_CAPACIDADE));
  });
  const tamanhoTexto = (sku) => [(valores['Diâmetro'] || {})[sku], (valores.Capacidade || {})[sku]]
    .filter(conhecido).join(' · ');
  const semFicha = carregado ? produtos.filter((p) => !(fichas[p.sku] || {}).fonte) : [];
  // Medida que a VTEX não tem e veio do site do fabricante (15/10/2026): a tela diz
  // de onde veio, com o link, para o atendente conferir. Não vai na imagem.
  const deFora = carregado ? produtos.flatMap((p) => {
    const f = fichas[p.sku] || {};
    return Object.entries(f.fontes_medidas || {}).map(([campo, fonte]) => ({
      p, campo, fonte, valor: (f.ficha || {})[campo], url: f.url_fabricante,
    }));
  }) : [];
  const comFalta = produtos.filter((p) => faltas.some((f) => f.p.sku === p.sku) && (fichas[p.sku] || {}).descricao);

  return (
    // stopPropagation: este modal abre por cima de outro; o clique no fundo fecha só este.
    <div className="fixed inset-0 z-[60] flex items-start justify-center bg-black/50 p-4 overflow-y-auto"
      onClick={(e) => { e.stopPropagation(); onClose(); }}>
      <div className="bg-white dark:bg-slate-900 rounded-xl shadow-2xl w-full max-w-5xl my-6 p-5 space-y-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="font-bold text-base">🖼️ Comparativo para o cliente</h2>
            <p className="text-xs text-slate-500 mt-0.5">Sem preço. Confira e complete antes de copiar: quem envia é você.</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" aria-label="Fechar"><X className="h-5 w-5" /></button>
        </div>

        {semOriginal ? (
          <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800 px-3 py-3 text-sm text-amber-900 dark:text-amber-200 flex items-start gap-1.5">
            <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <span>Este pedido está sem o SKU do produto original, e sem ele não dá para montar a imagem. Confira o item no ELO.</span>
          </div>
        ) : !carregado ? (
          <div className="py-10 text-center text-sm text-slate-500 animate-pulse">Lendo a ficha técnica na VTEX…</div>
        ) : (
          <>
            {voltDiferente.length > 0 && (
              <div className="rounded-md border border-red-300 bg-red-50 dark:bg-red-950/20 dark:border-red-800 px-3 py-2 text-xs text-red-800 dark:text-red-200 flex items-start gap-1.5">
                <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>
                  <strong>Voltagem diferente do original ({(valores.Voltagem || {})[produtos[0].sku]}):</strong>{' '}
                  {voltDiferente.map((p) => `${p.nome} (${(valores.Voltagem || {})[p.sku]})`).join('; ')}. Confira antes de ofertar.
                </span>
              </div>
            )}
            {menores.length > 0 && (
              <div className="rounded-md border border-red-300 bg-red-50 dark:bg-red-950/20 dark:border-red-800 px-3 py-2 text-xs text-red-800 dark:text-red-200 flex items-start gap-1.5">
                <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>
                  <strong>Bem menor que o pedido ({tamanhoTexto(produtos[0].sku)}):</strong>{' '}
                  {menores.map((p) => `${p.nome} (${tamanhoTexto(p.sku)})`).join('; ')}. Não é similar: tire da proposta.
                </span>
              </div>
            )}
            {deFora.length > 0 && (
              <div className="rounded-md border border-sky-200 bg-sky-50 dark:bg-sky-950/20 dark:border-sky-800 px-3 py-2 text-xs text-sky-900 dark:text-sky-200 flex items-start gap-1.5">
                <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>
                  <strong>Fora da VTEX:</strong>{' '}
                  {deFora.map((d, i) => (
                    <span key={`${d.p.sku}-${d.campo}`}>
                      {i > 0 && '; '}
                      {d.campo} de {d.p.sku} ({d.valor}) veio do {d.fonte}
                      {d.url && (
                        <>{' '}(<a href={d.url} target="_blank" rel="noopener noreferrer" className="underline">abrir</a>)</>
                      )}
                    </span>
                  ))}. Confira se quiser.
                </span>
              </div>
            )}
            {semFicha.length > 0 && (
              <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800 px-3 py-2 text-xs text-amber-900 dark:text-amber-200 flex items-start gap-1.5">
                <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>Não achei na VTEX: {semFicha.map((p) => p.sku).join(', ')}. Complete os dados à mão.</span>
              </div>
            )}
            {faltas.length > 0 && (
              <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-800 px-3 py-2 text-xs text-amber-900 dark:text-amber-200 flex items-start gap-1.5">
                <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>
                  <strong>Faltam {faltas.length} {faltas.length === 1 ? 'informação' : 'informações'}</strong>{' '}
                  ({faltas.slice(0, 6).map((f) => `${f.rot} de ${f.p.sku}`).join(', ')}{faltas.length > 6 ? '…' : ''}).
                  Complete na tabela (a descrição do produto está logo abaixo) ou tire a linha. O que ficar em branco sai como "não informado".
                </span>
              </div>
            )}

            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="border-b">
                    <th className="px-2 py-1.5 text-left bg-slate-50 dark:bg-slate-800 w-40">&nbsp;</th>
                    {produtos.map((p, i) => (
                      <th key={p.sku} className={`px-2 py-1.5 text-left align-top ${i === 0 ? 'bg-amber-50/40 dark:bg-amber-950/20' : ''}`}>
                        <div className="text-[10px] font-bold uppercase tracking-wide text-slate-500">{i === 0 ? 'Seu pedido' : `Alternativa ${i}`}</div>
                        <div className="font-semibold">{p.nome}</div>
                        <div className="font-mono text-[10px] text-slate-400">{p.sku}{p.id ? ` · ID ${p.id}` : ''}</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <th className="px-2 py-1 text-left font-medium text-slate-600 bg-slate-50 dark:bg-slate-800">
                      O que muda <span className="block text-[10px] font-normal text-slate-400">automático; pode editar</span>
                    </th>
                    {produtos.map((p, i) => (
                      <td key={p.sku} className={`px-2 py-1 ${i === 0 ? 'bg-amber-50/30 dark:bg-amber-950/10' : ''}`}>
                        {i === 0 ? <span className="text-slate-400">—</span> : (
                          <input
                            value={resumo[p.sku] || ''}
                            onChange={(e) => { const v = e.target.value; setResumoManual((prev) => ({ ...prev, [p.sku]: v })); }}
                            className="w-full rounded border border-orange-300 px-1.5 py-0.5 text-xs font-semibold text-orange-700 bg-white dark:bg-slate-900 dark:text-orange-300"
                          />
                        )}
                      </td>
                    ))}
                  </tr>
                  {(linhas || []).map((rot) => (
                    <tr key={rot} className="border-b">
                      <th className="px-2 py-1 text-left font-medium text-slate-600 bg-slate-50 dark:bg-slate-800">
                        <div className="flex items-center justify-between gap-1">
                          <span>{rot}</span>
                          <button onClick={() => tirarLinha(rot)} title="Tirar esta linha da imagem"
                            className="text-slate-400 hover:text-red-600" aria-label={`Tirar ${rot}`}><X className="h-3.5 w-3.5" /></button>
                        </div>
                      </th>
                      {produtos.map((p, i) => {
                        const v = (valores[rot] || {})[p.sku] || FALTA;
                        const falta = v === FALTA;
                        const muda = !falta && !!(destaque[rot] && destaque[rot][p.sku]);
                        return (
                          <td key={p.sku} className={`px-2 py-1 ${i === 0 ? 'bg-amber-50/30 dark:bg-amber-950/10' : ''}`}>
                            <input
                              value={falta ? '' : v}
                              placeholder="?"
                              onChange={(e) => mudar(rot, p.sku, e.target.value)}
                              title={muda ? 'Diferente do pedido: sai em laranja na imagem' : undefined}
                              className={`w-full rounded border px-1.5 py-0.5 text-xs bg-white dark:bg-slate-900 ${falta ? 'border-amber-400 bg-amber-50 dark:bg-amber-950/30' : (muda ? 'border-orange-300 font-semibold text-orange-700 dark:text-orange-300' : 'border-slate-200 dark:border-slate-700')}`}
                            />
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {extras.length > 0 && (
              <div className="flex flex-wrap items-center gap-1.5 text-xs">
                <span className="text-slate-500">Adicionar linha:</span>
                {extras.map((rot) => (
                  <button key={rot} onClick={() => porLinha(rot)}
                    className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full border border-slate-300 text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800">
                    <Plus className="h-3 w-3" />{rot}
                  </button>
                ))}
              </div>
            )}

            {comFalta.length > 0 && (
              <div className="space-y-1">
                {comFalta.map((p) => (
                  <div key={p.sku} className="border rounded-md">
                    <button onClick={() => setVerDesc((prev) => ({ ...prev, [p.sku]: !prev[p.sku] }))}
                      className="w-full text-left px-2.5 py-1.5 text-xs font-semibold hover:bg-slate-50 dark:hover:bg-slate-800">
                      {verDesc[p.sku] ? '▾' : '▸'} Descrição de {p.sku} na VTEX (para completar o que falta)
                    </button>
                    {verDesc[p.sku] && (
                      <pre className="px-2.5 pb-2 text-xs whitespace-pre-wrap font-sans text-slate-600 dark:text-slate-300 max-h-48 overflow-y-auto">{fichas[p.sku].descricao}</pre>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <button onClick={copiarImagem} disabled={!fotosProntas}
                  className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-green-600 text-white hover:bg-green-700 font-semibold disabled:opacity-50">
                  <Copy className="h-3.5 w-3.5" /> Copiar imagem
                </button>
                <button onClick={baixarImagem} disabled={!fotosProntas}
                  className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border border-slate-300 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 font-semibold disabled:opacity-50">
                  <Download className="h-3.5 w-3.5" /> Baixar imagem
                </button>
                <button onClick={copiarTexto}
                  className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full border border-green-300 bg-green-50 text-green-700 hover:bg-green-100 font-semibold">
                  <Copy className="h-3.5 w-3.5" /> Copiar texto
                </button>
                {!fotosProntas && <span className="text-xs text-slate-400 animate-pulse">carregando as fotos…</span>}
              </div>
              <div className="border rounded-lg p-2.5 space-y-1">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Texto para enviar junto com a imagem</div>
                <pre className="text-xs whitespace-pre-wrap font-sans text-slate-700 dark:text-slate-300 leading-relaxed">{textoCliente}</pre>
              </div>
              {preview && (
                <div className="border rounded-lg p-2 bg-slate-50 dark:bg-slate-800 overflow-x-auto">
                  <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500 mb-1">Como o cliente vai ver</div>
                  <img src={preview} alt="Comparativo que vai ao cliente" className="max-w-full h-auto rounded border bg-white" />
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
